import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  ApiResponse,
  ParcelCenterDownloadResponse,
  ParcelCenterUIResponse,
  ParcelCenterUISingleResponse,
} from "../../models/ApiResponse";
import agent from "../../api/agent";
import { ParcelCenterResponse } from "../../models/responseModels/parcelCenterResponse";
import {
  AddParcelCenterFdpMappingRequest,
  AddParcelCenterRequest,
  UpdateParcelCenterRequest,
} from "../../models/requestModels/parcelCenterRequest";

type InitialState = {
  isLoading: boolean;
  parcelCenterList: ParcelCenterResponse[];
  errorMessage: string | null;
};

const initialState: InitialState = {
  isLoading: false,
  parcelCenterList: [],
  errorMessage: null,
};

export const getparcelCenterList = createAsyncThunk<ParcelCenterUIResponse>(
  "parcelCenter/parcelCenterResponse",
  async () => {
    const response = await agent.ParcelCenterAgent.getParcelCenterList();
    return response;
  }
);

export const downloadParcelCenterDataToCsv = createAsyncThunk<
  ParcelCenterDownloadResponse,
  ParcelCenterResponse[]
>(
  "parcelCenter/downloadCsv",
  async (parcelCenterData: ParcelCenterResponse[]) => {
    const response = await agent.ParcelCenterAgent.parcelCenterCsvDownload(
      parcelCenterData
    );
    return response;
  }
);

export const downloadParcelCenterDataToXlsx = createAsyncThunk<
  Blob,
  ParcelCenterResponse[]
>(
  "parcelCenter/downloadXlsx",
  async (parcelCenterData: ParcelCenterResponse[]) => {
    const response = await agent.ParcelCenterAgent.parcelCenterXlsxDownload(
      parcelCenterData
    );
    return response;
  }
);

export const addParcelCenter = createAsyncThunk<
  ApiResponse,
  AddParcelCenterRequest
>(
  "parcelCenter/addParcelCenter",
  async (addParcelCenterRequest: AddParcelCenterRequest) => {
    const response = await agent.ParcelCenterAgent.addParcelCenter(
      addParcelCenterRequest
    );
    return response;
  }
);

export const addFdpMapping = createAsyncThunk<
  ParcelCenterUISingleResponse,
  AddParcelCenterFdpMappingRequest
>(
  "parcelCenter/addParcelCenter",
  async (
    addParcelCenterFdpMappingRequest: AddParcelCenterFdpMappingRequest
  ) => {
    const response = await agent.ParcelCenterAgent.addParcelCenterFdpMapping(
      addParcelCenterFdpMappingRequest
    );
    return response;
  }
);

export const editFdpMapping = createAsyncThunk<
  ParcelCenterUISingleResponse,
  AddParcelCenterFdpMappingRequest
>(
  "parcelCenter/editParcelCenter",
  async (
    addParcelCenterFdpMappingRequest: AddParcelCenterFdpMappingRequest
  ) => {
    const response = await agent.ParcelCenterAgent.editParcelCenterFdpMapping(
      addParcelCenterFdpMappingRequest
    );
    return response;
  }
);

export const updateParcelCenter = createAsyncThunk<
  ApiResponse,
  UpdateParcelCenterRequest
>(
  "parcelCenter/updateParcelCenter",
  async (updateParcelCenterRequest: UpdateParcelCenterRequest) => {
    const response = await agent.ParcelCenterAgent.updateParcelCenter(
      updateParcelCenterRequest
    );
    return response;
  }
);

const parcelCenterSlice = createSlice({
  name: "parcelCenter",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getparcelCenterList.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getparcelCenterList.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.isSuccess) {
          state.parcelCenterList = action.payload.data;
        } else {
          state.parcelCenterList = [];
        }
      })
      .addCase(addFdpMapping.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.isSuccess) {
          const response = action.payload.data;
          state.parcelCenterList = state.parcelCenterList.map((e) => {
            if (e.parcelCenterId === response.parcelCenterId) {
              return response;
            }
            return e;
          });
        } else {
          state.parcelCenterList = [];
        }
      })
      .addCase(editFdpMapping.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.isSuccess) {
          const response = action.payload.data;
          state.parcelCenterList = state.parcelCenterList.map((e) => {
            if (e.parcelCenterId === response.parcelCenterId) {
              return response;
            }
            return e;
          });
        } else {
          state.parcelCenterList = [];
        }
      })
      .addCase(getparcelCenterList.rejected, (state, action) => {
        state.isLoading = false;
        state.parcelCenterList = [];
        state.errorMessage = action.error.message!;
      });
  },
});

export default parcelCenterSlice.reducer;
